import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    CustomInput,
    Spinner,
    FormFeedback,
    FormText,
    Row,
    Col
} from 'reactstrap';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { createOil } from './redux/dispatcher';

const AddOil = ({ args }) => {
    const dispatch = useDispatch();
    const oilList = useSelector((state) => state.oilListReducer);
    const companyListData = useSelector((state) => state.companyListReducer);
    const [modal, setModal] = useState(false);
    const [companyList, setCompanyList] = useState(companyListData?.data?.data || []);

    useEffect(() => {
        setCompanyList(companyListData?.data?.data || []);
    }, [companyListData]);

    const toggleModal = () => {
        setModal(!modal);
        formik.resetForm();
    };

    const validationSchema = yup.object().shape({
        oilType: yup.string().required('Please select oil type.'),
        brand: yup
            .string()
            .min(2, 'Brand name must be at least 2 characters')
            .max(20, 'Brand name must be less than 20 characters')
            .required('Please enter a brand name.'),
        packingSize: yup
            .string()
            .min(2, 'Product name must be at least 2 characters')
            .max(50, 'Product name must be less than 50 characters')
            .required('Please enter a product name.'),
        price: yup
            .number()
            .positive('Price must be a positive number.')
            .required('Please enter a price.'),
        ri: yup
            .number()
            .typeError('RI must be a number.')
            .positive('RI must be a positive number.')
            .test('max-decimal-places', 'RI must have at most 2 decimal places.', (value) => {
                if (value === undefined || value === null || value === '') return true;
                return /^\d+(\.[0-9]{1,2})?$/.test(String(value));
            })
            .nullable()
            .notRequired(),
        companyName: yup.string().required('Please select a company.')
    });

    const formik = useFormik({
        initialValues: {
            oilType: '',
            brand: '',
            packingSize: '',
            price: '',
            ri: '',
            companyName: '',
            status: true,
            file: null,
            testFile: null
        },
        validationSchema,
        onSubmit: async (values) => {
            const formData = new FormData();
            formData.append('price', values.price);
            formData.append('product_name', values.packingSize);
            formData.append('status', values.status ? 'active' : 'inactive');
            formData.append('company_id', values.companyName);
            formData.append('type', values.oilType);
            formData.append('brand_name', values.brand);
            if (values.ri !== '' && values.ri !== null && values.ri !== undefined) {
                const roundedRI = parseFloat(values.ri).toFixed(2);
                formData.append('ri', roundedRI);
            }
            if (values.file) formData.append('msds_file', values.file);
            if (values.testFile) formData.append('test_cert_file', values.testFile);

            dispatch(createOil(formData, toggleModal));
        }
    });

    const handleFileChange = (e, setFieldValue) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setFieldValue('file', file);
        } else {
            setFieldValue('file', null);
            toast.error('Please select a valid PDF file.');
        }
    };

    const handleTestFileChange = (e, setFieldValue) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setFieldValue('testFile', file);
        } else {
            setFieldValue('testFile', null);
            toast.error('Please select a valid PDF file.');
        }
    };

    const handleRiChange = (e) => {
        let { value } = e.target;
        // Allow only digits and one dot
        value = value.replace(/[^0-9.]/g, '');
        const parts = value.split('.');
        if (parts.length > 2) {
            // More than one dot, remove extras
            value = parts[0] + '.' + parts.slice(1).join('');
        }
        const [intPart, decPart] = value.split('.');
        if (decPart && decPart.length > 2) {
            value = intPart + '.' + decPart.slice(0, 2);
        }
        formik.setFieldValue('ri', value);
    };

    const handleRiBlur = () => {
        const raw = formik.values.ri;
        if (raw === '' || raw === null || raw === undefined) return;
        const num = parseFloat(raw);
        if (!isNaN(num)) {
            formik.setFieldValue('ri', num.toFixed(2));
        }
        formik.setFieldTouched('ri', true, true);
    };

    return (
        <div>
            <Button color="primary" onClick={toggleModal}>
                Add Oil
            </Button>

            <Modal isOpen={modal} toggle={toggleModal} {...args} modalClassName="custom-modal" style={{ maxWidth: '40%' }}>
                <h1 style={{ textAlign: 'center', borderBottom: '4px solid #135674', color: '#135674' }} className="m-3">
                    Add Oil
                </h1>
                <ModalBody>
                    <Form onSubmit={formik.handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="oilTypeSelect">Select Type<span style={{ color: 'red' }}>*</span></Label>
                                    <Input
                                        type="select"
                                        name="oilType"
                                        id="oilTypeSelect"
                                        value={formik.values.oilType}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        invalid={formik.touched.oilType && !!formik.errors.oilType}
                                    >
                                        <option value="">Select Type</option>
                                        <option value="Coolant">Coolant Oil</option>
                                        {/* <option value="Maintenance">Maintenance Oil</option> */}
                                        <option value="Maintenance">Maintenance Oil</option>
                                        <option value="Hydraulic">Hydraulic Oil</option>
                                    </Input>
                                    <FormFeedback>{formik.errors.oilType}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="brand">Brand Name<span style={{ color: 'red' }}>*</span></Label>
                                    <Input
                                        type="text"
                                        name="brand"
                                        id="brand"
                                        value={formik.values.brand}
                                        placeholder="Enter Brand Name"
                                        invalid={formik.touched.brand && !!formik.errors.brand}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <FormFeedback>{formik.errors.brand}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="packingSize">Product Name<span style={{ color: 'red' }}>*</span></Label>
                                    <Input
                                        type="text"
                                        name="packingSize"
                                        id="packingSize"
                                        value={formik.values.packingSize}
                                        placeholder="Enter Product Name"
                                        invalid={formik.touched.packingSize && !!formik.errors.packingSize}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <FormFeedback>{formik.errors.packingSize}</FormFeedback>
                                </FormGroup>
                            </Col>
                            {formik.values.oilType === 'Coolant' && (
                                <Col md={6}>
                                    <FormGroup>
                                        <Label for="ri">RI</Label>
                                        <Input
                                            type="text"
                                            name="ri"
                                            id="ri"
                                            value={formik.values.ri}
                                            placeholder="Enter RI"
                                            invalid={formik.touched.ri && !!formik.errors.ri}
                                            onChange={handleRiChange}
                                            onBlur={handleRiBlur}
                                        />
                                        <FormFeedback>{formik.errors.ri}</FormFeedback>
                                    </FormGroup>
                                </Col>
                            )}
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="companyName">Select Company<span style={{ color: 'red' }}>*</span></Label>
                                    <Input
                                        type="select"
                                        name="companyName"
                                        id="companyName"
                                        value={formik.values.companyName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        invalid={formik.touched.companyName && !!formik.errors.companyName}
                                    >
                                        <option value="">Select company</option>
                                        {companyList.map((item) => (
                                            <option key={item?.id} value={item?.id}>
                                                {item?.company_name}
                                            </option>
                                        ))}
                                    </Input>
                                    <FormFeedback>{formik.errors.companyName}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="price">Price<span style={{ color: 'red' }}>*</span> (per Liter)</Label>
                                    <Input
                                        type="text"
                                        name="price"
                                        id="price"
                                        value={formik.values.price}
                                        placeholder="Enter Price"
                                        invalid={formik.touched.price && !!formik.errors.price}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <FormFeedback>{formik.errors.price}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>



                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="upload" style={{ borderBottom: '2px solid grey' }}>
                                        Attach MSDS File in PDF
                                    </Label>
                                    <Input
                                        type="file"
                                        id="upload"
                                        accept="application/pdf"
                                        onChange={(e) => handleFileChange(e, formik.setFieldValue)}
                                        hidden
                                    />
                                    <Label
                                        for="upload"
                                        className="btn btn-primary ml-2"
                                    >
                                        <i className="fa-solid fa-upload"></i>
                                    </Label>
                                    {formik.values.file && (
                                        <FormText color="muted">{formik.values.file.name}</FormText>
                                    )}
                                </FormGroup>
                            </Col>

                            <Col md={6}>
                                <FormGroup>
                                    <Label for="uploadTestCertificate" style={{ borderBottom: '2px solid grey' }}>
                                        Attach Test Certificate
                                    </Label>
                                    <Input
                                        type="file"
                                        id="uploadTestCertificate"
                                        accept="application/pdf"
                                        onChange={(e) => handleTestFileChange(e, formik.setFieldValue)}
                                        hidden
                                    />
                                    <Label
                                        for="uploadTestCertificate"
                                        className="btn btn-primary ml-2"
                                    >
                                        <i className="fa-solid fa-upload"></i>
                                    </Label>
                                    {formik.values.testFile && (
                                        <FormText color="muted">{formik.values.testFile.name}</FormText>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <CustomInput
                                type="switch"
                                id="statusSwitch"
                                name="status"
                                label="Status"
                                checked={formik.values.status}
                                onChange={formik.handleChange}
                            />
                        </FormGroup>

                        <ModalFooter>
                            <Button
                                type="submit"
                                color="primary"
                                disabled={oilList?.isLoading}
                            >
                                {oilList?.isLoading ? (
                                    <>
                                        <Spinner size="sm" />
                                        <span>&nbsp; Adding...</span>
                                    </>
                                ) : (
                                    'Add'
                                )}
                            </Button>
                            <Button color="secondary" onClick={toggleModal}>
                                Cancel
                            </Button>
                        </ModalFooter>
                    </Form>
                </ModalBody>
            </Modal>
        </div>
    );
};

export default AddOil;
